import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { ExternalLink, ShieldCheck, Lock, FileSignature } from 'lucide-react';

// ─── a frontier AI lab's five-stage authority flow ─────────────────────────────
const STAGES = [
  {
    n: 1,
    stage: 'Assist',
    anthropic: 'Shopping agent answers questions grounded in the merchant catalog.',
    kojiru: 'Not applicable. No authority is exercised at this stage.',
    kojiruLevel: 'none',
  },
  {
    n: 2,
    stage: 'Recommend',
    anthropic: 'Agent surfaces products, prices, and options within the retailer catalog.',
    kojiru: 'Not applicable. No authority is exercised at this stage.',
    kojiruLevel: 'none',
  },
  {
    n: 3,
    stage: 'Propose',
    anthropic: 'Agent assembles a cart or a staged order.',
    kojiru:
      'A KYPA credential is drafted (encoding max amount, recipient allow-list, category, and expiry) but is not yet signed by the principal. The mandate is legible before authorization.',
    kojiruLevel: 'partial',
  },
  {
    n: 4,
    stage: 'Confirm',
    anthropic: 'A human approves the cart, or grants a scoped standing authority.',
    kojiru:
      'The principal signs the credential (EIP-712). Consent is now cryptographically recoverable from the signature, not inferred from a UI event log.',
    kojiruLevel: 'full',
  },
  {
    n: 5,
    stage: 'Authorize',
    anthropic:
      'Payment executes. In the blueprint, this step lives outside the shopping agent and is treated as a boolean gate.',
    kojiru:
      "Merchant verifies the KYPA credential at a single endpoint. If the agent's on-chain pledge has been slashed, the credential is refused before settlement. If a harm event lands post-authorization, it is recorded publicly next to the operator's pledge. Kojiru does not pay claims. Authorization becomes a cryptographic instrument, not a boolean.",
    kojiruLevel: 'full',
  },
];

// ─── The three-property triad ──────────────────────────────────────────
const PROPERTIES = [
  {
    n: 1,
    icon: FileSignature,
    label: 'Traceable consent',
    body: "Every credential is signed at issuance and encodes the consumer as principal. Consent is cryptographically recoverable from the signature, not inferred from a UI event log.",
    accent: 'cyan',
  },
  {
    n: 2,
    icon: Lock,
    label: 'Bounded authority',
    body: "The mandate block encodes max amount, recipient allow-list, category, and delivery target: signed into the credential itself. Out-of-scope requests are refused at the merchant edge before value moves.",
    accent: 'emerald',
  },
  {
    n: 3,
    icon: ShieldCheck,
    label: 'Evidence-preserving execution',
    body: "Every settled request and every slash produces a signed JSON packet, anchored to the block hash. Auditable by counterparty, regulator, or underwriter without asking permission.",
    accent: 'amber',
  },
];

const accentClasses = {
  cyan: 'border-cyan-500/30 bg-cyan-500/5 text-cyan-300',
  emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300',
  amber: 'border-amber-500/30 bg-amber-500/5 text-amber-300',
};

const levelBadge = {
  none: { text: 'N/A', cls: 'bg-slate-800 text-slate-500 border-slate-700' },
  partial: { text: 'DRAFT', cls: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30' },
  full: { text: 'SIGNED', cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' },
};

export default function ClaudeCommerceAgents() {
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="claude-commerce-agents-page">
      <Helmet>
        <title>The Five-Stage Flow: Where Authorization Becomes Verifiable</title>
        <meta
          name="description"
          content="A frontier AI lab&rsquo;s Commerce Agents blueprint codifies the five-stage authority flow every enterprise agentic-commerce architecture will follow. Kojiru's KYPA is the primitive that makes stage 5 cryptographically verifiable rather than a boolean gate."
        />
      </Helmet>

      <Navbar />

      {/* HERO */}
      <section className="pt-24 pb-14 px-6 border-b border-white/10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-[10px] font-bold tracking-[2.4px] text-amber-400 mb-4">
              THE FIVE-STAGE FLOW &middot; MAPPED
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-6 tracking-tight">
              Enterprise agentic commerce is <span className="text-amber-400">a five-stage flow</span>.
              <br />
              Kojiru completes stage 5.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-4">
              In September 2026 a frontier AI lab published <em>The lab&rsquo;s Commerce Agents blueprint</em> a
              forkable, open blueprint that codifies the authority-stage separation the largest
              enterprise deployments of commerce agents have arrived at in production. The
              blueprint distinguishes five stages of agent authority: <strong className="text-white">Assist,
              Recommend, Propose, Confirm, Authorize.</strong> The first four are language-model
              work. Stage 5 is where the instrument either exists or does not.
            </p>
            <p className="text-sm text-slate-500 max-w-3xl">
              Every claim on this page is either a citation of a frontier AI lab's public blueprint,
              a citation of a public governance-architecture comment thread, or an on-chain
              transaction. Every fact is independently verifiable in under a minute.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FIVE-STAGE MAPPING */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-black mb-3 tracking-tight">
            Every stage in the blueprint. And where the Kojiru primitive lives.
          </h2>
          <p className="text-sm text-slate-400 mb-10 max-w-3xl leading-relaxed">
            A frontier AI lab's blueprint holds payment outside the shopping agent and requires human
            approval for consequential actions. Kojiru does not change that. It adds one property
            at stage 5: the authorization step is a signed, scope-bounded, on-chain-collateralized
            instrument, not a boolean.
          </p>

          <div className="space-y-4">
            {STAGES.map((s) => (
              <div
                key={s.n}
                className="border border-white/10 rounded-xl overflow-hidden bg-slate-900/50"
                data-testid={`stage-${s.n}`}
              >
                <div className="p-5 border-b border-white/5 flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center font-bold text-amber-400">
                    {s.n}
                  </div>
                  <div className="flex-1">
                    <div className="text-lg font-bold text-white leading-tight">{s.stage}</div>
                  </div>
                  <div
                    className={`text-[10px] font-bold tracking-[1.4px] px-2.5 py-1 rounded-md border ${levelBadge[s.kojiruLevel].cls}`}
                  >
                    KYPA: {levelBadge[s.kojiruLevel].text}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-white/5">
                  <div className="p-5">
                    <div className="text-[10px] font-bold tracking-[1.6px] text-slate-500 mb-2">
                      FRONTIER LAB BLUEPRINT
                    </div>
                    <div
                      className="text-sm text-slate-300 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: s.anthropic }}
                    />
                  </div>
                  <div className="p-5 bg-emerald-950/20">
                    <div className="text-[10px] font-bold tracking-[1.6px] text-emerald-400 mb-2">
                      KOJIRU'S PRIMITIVE AT THIS STAGE
                    </div>
                    <div
                      className="text-sm text-slate-200 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: s.kojiru }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THREE-PROPERTY TRIAD */}
      <section className="py-14 px-6 bg-slate-900/50 border-y border-white/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">
            THE PROPERTY TRIAD
          </div>
          <h2 className="text-2xl sm:text-3xl font-black mb-3 leading-tight max-w-3xl">
            Traceable consent. Bounded authority. Evidence-preserving execution.
          </h2>
          <p className="text-sm text-slate-400 mb-8 max-w-3xl leading-relaxed">
            The three properties an AI-governance architect writing under the blueprint launch
            named as the load-bearing requirements of any trustworthy agentic-commerce system.
            Each is a property KYPA delivers by construction, not by policy.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {PROPERTIES.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.n}
                  className={`border rounded-xl p-5 ${accentClasses[p.accent]}`}
                  data-testid={`triad-${p.n}`}
                >
                  <Icon className="w-6 h-6 mb-3" />
                  <div className="text-[10px] font-bold tracking-[1.6px] mb-2 opacity-70">
                    0{p.n}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">{p.label}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* POSITIONING */}
      <section className="py-14 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">POSITIONING</div>
          <h2 className="text-2xl font-black text-white mb-4 leading-tight">
            Composes cleanly under the blueprint. Not against it.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-4">
            Kojiru does not compete with the lab&rsquo;s Commerce Agents blueprint, or the major cloud AI platforms,
            from any vendor. The KYPA credential is a header extension: {' '}
            <code className="text-cyan-300 text-xs">X-KYPA-Credential</code> that rides in
            the same session as any x402 or standard HTTP payment protocol. Every one of the
            merchant-agent implementations in a frontier AI lab's blueprint (retail, travel, telecom,
            entertainment) can adopt it with a single additional check in the checkout webhook,
            and one on-chain read against Base.
          </p>
          <p className="text-slate-300 leading-relaxed mb-4">
            The commercial effect: enterprise deployments running the lab&rsquo;s Commerce Agents blueprint on any
            cloud can turn their stage-5 authorization step from a policy commitment into an
            arithmetic property. Bypass stops being a governance-review finding and becomes
            cryptographically impossible.
          </p>
          <p className="text-slate-300 leading-relaxed">
            Model and platform providers cap liability at fees paid. When an agent crosses an
            organizational boundary and causes harm, the counterparty sues the deployer, not the
            model provider. Kojiru is where the deployer moves that exposure. The blueprint
            handles the first four stages beautifully; Kojiru is the fifth.
          </p>
        </div>
      </section>

      {/* WHAT THIS PAGE DOES NOT CLAIM */}
      <section className="py-14 px-6 bg-slate-900/40">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-red-400 mb-3">
            WHAT THIS PAGE DOES NOT CLAIM
          </div>
          <h2 className="text-2xl font-black text-white mb-6 leading-tight">
            The honest register: same treatment as our /readiness page.
          </h2>
          <ul className="space-y-3 text-sm text-slate-300 leading-relaxed">
            <li className="flex gap-3">
              <span className="text-red-400 flex-shrink-0">&rarr;</span>
              <div>
                Kojiru is not affiliated with a frontier AI lab, endorsed by a frontier AI lab, or a listed
                partner in the lab&rsquo;s Commerce Agents repository. This page maps our primitive
                onto their publicly-released architecture, on our own initiative.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-red-400 flex-shrink-0">&rarr;</span>
              <div>
                The three-property triad ("traceable consent, bounded authority,
                evidence-preserving execution") was articulated by an AI-governance architect
                writing publicly in September 2026, unrelated to Kojiru. We adopted the language
                because it correctly names what our instrument delivers; we do not claim to have
                coined it.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-red-400 flex-shrink-0">&rarr;</span>
              <div>
                The first live slash executed on Base Sepolia, a public test network, using a
                Kojiru-branded mock ERC-20. The USDC-bound mainnet Hook is in advisor-led
                deployment as of August 2026 and will be reported on-chain when it lands.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-red-400 flex-shrink-0">&rarr;</span>
              <div>
                No enterprise deployment of the lab&rsquo;s Commerce Agents blueprint has integrated the KYPA
                credential yet. This page argues the integration is trivial and the value case is
                clear: that argument has not yet been settled by the market.
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* SOURCES */}
      <section className="py-12 px-6 bg-slate-900/60 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-slate-500 mb-4">
            SOURCES &middot; ALL PUBLIC
          </div>
          <ul className="space-y-2 text-xs text-slate-400 leading-relaxed">
            <li>
              <strong className="text-slate-200">A frontier AI lab: Commerce Agents blueprint</strong>{' '}
              (open-source, forkable reference blueprint, September 2026). Published by a frontier AI lab
              on the lab&rsquo;s business channel, released under the frontier AI lab public blueprint
              license.
            </li>
            <li>
              <strong className="text-slate-200">Kojiru on-chain slash</strong> Base
              Sepolia, chain ID 84532, KojiruACSHook{' '}
              <code className="text-slate-300">0x5662668FB18bc504e05f4809005f8caa104103BA</code>.
              Slash tx{' '}
              <code className="text-slate-300 break-all">
                0x694ffe5f5a35dbf8e1267110987f802ec6b4173772c4627f7495ca2e2ef1d276
              </code>
              , block 43445800, 26 August 2026.
            </li>
            <li>
              <strong className="text-slate-200">KYPA Credential Envelope v1.0</strong> {' '}
              <Link to="/kypa/instrument" className="text-cyan-300 hover:underline">
                kojiru.com/kypa/instrument
              </Link>
              . Facilitator integration brief:{' '}
              <a
                href="/downloads/kypa-facilitator-integration-brief.pdf"
                className="text-cyan-300 hover:underline"
              >
                one-page PDF
              </a>
              .
            </li>
            <li>
              <strong className="text-slate-200">Enterprise readiness self-audit</strong> {' '}
              <Link to="/readiness" className="text-cyan-300 hover:underline">
                kojiru.com/readiness
              </Link>
              . Underwriter's five questions:{' '}
              <Link to="/vs/agent-insurance-questionnaire" className="text-cyan-300 hover:underline">
                kojiru.com/vs/agent-insurance-questionnaire
              </Link>
              .
            </li>
          </ul>
          <p className="text-xs text-slate-500 mt-6 italic">
            Third-party names on this page are limited to institutional public-record actors
            (a frontier AI lab as the publisher of a released blueprint). No private commentators are
            named, consistent with Kojiru's standing third-party naming policy.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
